import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Api, { getApiUrl } from '../../services/api';
import { useAtozasTranslate } from '../../context/AtozasLanguageContext';

import LanguageSelector from './LanguageSelector';
import ConfirmationModal from './ConfirmationModal';

const CACHE_TTL_MS = 5 * 60 * 1000;

const sidebarDataCache = {
  driverKey: null,
  driverProfile: null,
  userKey: null,
  userProfile: null,
  ratingKey: null,
  rating: null,
  fetchedAt: 0,
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const apiUrl = getApiUrl();
  const cleanPath = imagePath.replace(/^\/+/, '').replace(/\\/g, '/');
  return `${apiUrl}/${cleanPath}`;
};

const StarRating = memo(function StarRating({ ratingValue, total }) {
  const stars = useMemo(() => {
    const items = [];
    const fullStars = Math.floor(ratingValue);
    const hasHalfStar = ratingValue % 1 >= 0.5;

    for (let i = 0; i < 5; i += 1) {
      if (i < fullStars) {
        items.push(
          <span key={i} className="text-yellow-400 text-base notranslate" translate="no">★</span>
        );
      } else if (i === fullStars && hasHalfStar) {
        items.push(
          <span key={i} className="text-yellow-400 text-base notranslate" translate="no">☆</span>
        );
      } else {
        items.push(
          <span key={i} className="text-gray-300 text-base notranslate" translate="no">☆</span>
        );
      }
    }
    return items;
  }, [ratingValue]);

  return (
    <div className="notranslate mt-2 flex items-center gap-1" translate="no">
      {stars}
      {total > 0 && (
        <span className="notranslate ml-1 text-xs text-slate-500" translate="no">
          ({total})
        </span>
      )}
    </div>
  );
});

const UDsidebar = ({
  isOpen,
  onClose,
  user,
  driver,
  isDriver = false,
  onHome,
  onProfile,
  onRides,
  onLogout,
  onDriverTabChange,
  activeDriverTab,
  onBecomeDriver,
  onDriverDashboard,
  onUserDashboard,
}) => {
  const [driverProfile, setDriverProfile] = useState(driver || null);
  const [userProfile, setUserProfile] = useState(user?.profilePicture ? user : null);
  const [rating, setRating] = useState({ average: 0, total: 0 });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isAnimatedOpen, setIsAnimatedOpen] = useState(false);

  const hasMountedRef = useRef(false);
  const fetchGenRef = useRef(0);
  const bodyLockRef = useRef(null);

  const tLanguage = useAtozasTranslate('Language');
  const tEditProfile = useAtozasTranslate('Edit Profile');
  const tDashboard = useAtozasTranslate('Dashboard');
  const tMyRides = useAtozasTranslate('My Rides');
  const tEarnings = useAtozasTranslate('Earnings');
  const tAccount = useAtozasTranslate('Account');
  const tHome = useAtozasTranslate('Home');
  const tProfile = useAtozasTranslate('Profile');
  const tUserDashboard = useAtozasTranslate('User Dashboard');
  const tDriverDashboard = useAtozasTranslate('Driver Dashboard');
  const tBecomeDriver = useAtozasTranslate('Become Driver');
  const tLogout = useAtozasTranslate('Logout');

  useEffect(() => {
    if (driver) setDriverProfile(driver);
  }, [driver]);

  useEffect(() => {
    if (user?.profilePicture) setUserProfile(user);
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      hasMountedRef.current = true;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsAnimatedOpen(false);
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    bodyLockRef.current = { previousOverflow, previousTouchAction };

    const raf = requestAnimationFrame(() => {
      setIsAnimatedOpen(true);
    });

    return () => {
      cancelAnimationFrame(raf);
      if (bodyLockRef.current) {
        document.body.style.overflow = bodyLockRef.current.previousOverflow || '';
        document.body.style.touchAction = bodyLockRef.current.previousTouchAction || '';
        bodyLockRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const generation = fetchGenRef.current + 1;
    fetchGenRef.current = generation;

    const now = Date.now();
    const cacheValid = now - sidebarDataCache.fetchedAt < CACHE_TTL_MS;
    const userId = user?.id || user?._id;
    const ratingKey = isDriver ? `driver:${user?.driverId || ''}` : `user:${userId || ''}`;

    if (cacheValid && sidebarDataCache.ratingKey === ratingKey && sidebarDataCache.rating) {
      setRating(sidebarDataCache.rating);
    }
    if (cacheValid && !isDriver && sidebarDataCache.userKey === userId && sidebarDataCache.userProfile) {
      setUserProfile(sidebarDataCache.userProfile);
    }
    if (cacheValid && isDriver && sidebarDataCache.driverKey === user?.driverId && sidebarDataCache.driverProfile) {
      setDriverProfile(sidebarDataCache.driverProfile);
    }

    const timeoutId = window.setTimeout(async () => {
      const token = localStorage.getItem('uttaapp_token');
      if (!token || fetchGenRef.current !== generation) return;

      const requests = [];

      if (isDriver && user?.driverId && !driverProfile?.profilePicture) {
        const driverKey = user.driverId;
        if (!(cacheValid && sidebarDataCache.driverKey === driverKey && sidebarDataCache.driverProfile)) {
          requests.push(
            axios
              .get(Api.Driver.profile(user.driverId), {
                headers: { Authorization: `Bearer ${token}` },
              })
              .then((response) => {
                if (response.data?.success && response.data.driver) {
                  sidebarDataCache.driverKey = driverKey;
                  sidebarDataCache.driverProfile = response.data.driver;
                  sidebarDataCache.fetchedAt = Date.now();
                  if (fetchGenRef.current === generation) {
                    setDriverProfile(response.data.driver);
                  }
                }
              })
              .catch(() => {})
          );
        }
      }

      if (!isDriver && !user?.profilePicture && userId) {
        if (!(cacheValid && sidebarDataCache.userKey === userId && sidebarDataCache.userProfile)) {
          requests.push(
            axios
              .get(Api.User.profile, {
                headers: { Authorization: `Bearer ${token}` },
              })
              .then((response) => {
                if (response.data?.success && response.data.user) {
                  sidebarDataCache.userKey = userId;
                  sidebarDataCache.userProfile = response.data.user;
                  sidebarDataCache.fetchedAt = Date.now();
                  if (fetchGenRef.current === generation) {
                    setUserProfile(response.data.user);
                  }
                }
              })
              .catch(() => {})
          );
        }
      }

      if (!(cacheValid && sidebarDataCache.ratingKey === ratingKey && sidebarDataCache.rating)) {
        if (isDriver && user?.driverId) {
          requests.push(
            axios
              .get(Api.Driver.rating, {
                headers: { Authorization: `Bearer ${token}` },
              })
              .then((response) => {
                const nextRating = response.data?.success
                  ? {
                      average: response.data.averageRating || 0,
                      total: response.data.totalRatings || 0,
                    }
                  : { average: 0, total: 0 };
                sidebarDataCache.ratingKey = ratingKey;
                sidebarDataCache.rating = nextRating;
                sidebarDataCache.fetchedAt = Date.now();
                if (fetchGenRef.current === generation) setRating(nextRating);
              })
              .catch(() => {
                if (fetchGenRef.current === generation) setRating({ average: 0, total: 0 });
              })
          );
        } else if (!isDriver && userId) {
          requests.push(
            axios
              .get(Api.Rating.getUserRatings(userId), {
                headers: { Authorization: `Bearer ${token}` },
              })
              .then((response) => {
                const nextRating = response.data?.success
                  ? {
                      average: parseFloat(response.data.averageRating) || 0,
                      total: response.data.totalRatings || 0,
                    }
                  : { average: 0, total: 0 };
                sidebarDataCache.ratingKey = ratingKey;
                sidebarDataCache.rating = nextRating;
                sidebarDataCache.fetchedAt = Date.now();
                if (fetchGenRef.current === generation) setRating(nextRating);
              })
              .catch(() => {
                if (fetchGenRef.current === generation) setRating({ average: 0, total: 0 });
              })
          );
        }
      }

      await Promise.all(requests);
    }, 16);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, isDriver, user, driverProfile?.profilePicture]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const currentDriver = driverProfile || driver;
  const currentUser = userProfile || user;
  const userName = currentUser?.username || currentUser?.name || 'User';
  const driverName = currentDriver?.name || currentUser?.username || currentUser?.name || 'Driver';

  const profilePictureUrl = useMemo(() => {
    if (isDriver) {
      return getImageUrl(
        currentDriver?.profilePicture || currentDriver?.profile_picture || currentDriver?.photo
      );
    }
    return getImageUrl(
      currentUser?.profilePicture || currentUser?.profile_picture || currentUser?.photo
    );
  }, [isDriver, currentDriver, currentUser]);

  const menuItemClass = (active) =>
    `flex w-full items-center justify-between rounded-[18px] px-3 py-2.5 text-left touch-manipulation transition-colors ${
      active ? 'bg-[#e8f6f1] text-[#0f766e]' : 'text-slate-700 active:bg-slate-50'
    }`;

  const iconWrapClass = (active) =>
    `inline-flex h-10 w-10 items-center justify-center rounded-2xl ${
      active ? 'bg-white text-[#0f766e]' : 'bg-slate-100 text-slate-500'
    }`;

  if (!hasMountedRef.current && !isOpen) {
    return null;
  }

  const panelOpen = isOpen && isAnimatedOpen;

  const sidebarContent = (
    <>
      <div
        className={`fixed inset-0 z-[9998] bg-slate-950/35 md:hidden transition-opacity duration-300 ease-out ${
          panelOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={handleClose}
        aria-hidden={!panelOpen}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-hidden={!panelOpen}
        className={`fixed left-0 top-0 z-[9999] flex h-[100dvh] w-[min(86vw,320px)] flex-col rounded-r-[30px] bg-[#f5f7f6] shadow-[18px_0_60px_-34px_rgba(15,23,42,0.55)] ring-1 ring-slate-200/80 will-change-transform md:hidden transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
          panelOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
        }`}
        style={{
          paddingTop: 'max(12px, env(safe-area-inset-top))',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        }}
      >
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]"
          style={{ contain: 'content' }}
        >
          <div className="mx-3 overflow-hidden rounded-[26px] bg-white text-slate-950 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center justify-between px-4 pb-2.5 pt-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0f766e]">
                  {isDriver ? 'Driver Space' : 'Rider Space'}
                </div>
                <div className="mt-0.5 text-lg font-bold">UttaApp</div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-9 w-9 touch-manipulation items-center justify-center rounded-full bg-slate-100 text-slate-500 active:bg-slate-200"
                aria-label="Close menu"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mx-3 mb-3 rounded-[22px] bg-[#f2fbf7] px-3.5 py-3.5 ring-1 ring-emerald-100">
              <div className="flex items-center gap-3.5">
                <div className="relative flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-[22px] bg-emerald-100 text-xl font-semibold text-[#0f766e]">
                  {profilePictureUrl ? (
                    <img
                      src={profilePictureUrl}
                      alt={isDriver ? driverName : userName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="profile-fallback h-full w-full items-center justify-center bg-emerald-100"
                    style={{ display: profilePictureUrl ? 'none' : 'flex' }}
                  >
                    {isDriver ? driverName.charAt(0).toUpperCase() : userName.charAt(0).toUpperCase()}
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-slate-500">
                    {isDriver ? 'Active driver account' : 'Personal account'}
                  </div>
                  <h3 className="mt-1 truncate text-xl font-bold text-slate-950">
                    {isDriver ? driverName : userName}
                  </h3>
                  {rating.average > 0 && (
                    <StarRating ratingValue={rating.average} total={rating.total} />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (isDriver && onDriverTabChange) {
                        onDriverTabChange('account');
                      } else if (onProfile) {
                        onProfile();
                      }
                      handleClose();
                    }}
                    className="mt-3 inline-flex touch-manipulation items-center rounded-full bg-[#0f766e] px-3.5 py-1.5 text-sm font-semibold text-white active:bg-[#0b5f58]"
                  >
                    {tEditProfile}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3 px-3 pb-3 pt-3">
            <div className="flex items-center justify-between gap-3 rounded-[22px] bg-white px-3.5 py-2.5 shadow-sm ring-1 ring-slate-200">
              <span className="text-sm font-semibold text-slate-700">{tLanguage}</span>
              <LanguageSelector
                aria-label={tLanguage}
                title={tLanguage}
                variant="pill"
                showSelectedCode
                className="shrink-0 border-slate-200 bg-slate-50 px-2.5 py-1.5 text-slate-700 shadow-none"
              />
            </div>

            {isDriver ? (
              <>
                <div className="px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  Driver Menu
                </div>
                <div className="space-y-1 rounded-[22px] bg-white p-2 shadow-sm ring-1 ring-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      onDriverTabChange?.('dashboard');
                      handleClose();
                    }}
                    className={menuItemClass(activeDriverTab === 'dashboard')}
                  >
                    <span className="flex items-center gap-3">
                      <span className={iconWrapClass(activeDriverTab === 'dashboard')}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </span>
                      <span className="text-[15px] font-medium">{tDashboard}</span>
                    </span>
                    <ChevronIcon />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onDriverTabChange?.('rides');
                      handleClose();
                    }}
                    className={menuItemClass(activeDriverTab === 'rides')}
                  >
                    <span className="flex items-center gap-3">
                      <span className={iconWrapClass(activeDriverTab === 'rides')}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </span>
                      <span className="text-[15px] font-medium">{tMyRides}</span>
                    </span>
                    <ChevronIcon />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onDriverTabChange?.('earnings');
                      handleClose();
                    }}
                    className={menuItemClass(activeDriverTab === 'earnings')}
                  >
                    <span className="flex items-center gap-3">
                      <span className={iconWrapClass(activeDriverTab === 'earnings')}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      <span className="text-[15px] font-medium">{tEarnings}</span>
                    </span>
                    <ChevronIcon />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onDriverTabChange?.('account');
                      handleClose();
                    }}
                    className={menuItemClass(activeDriverTab === 'account')}
                  >
                    <span className="flex items-center gap-3">
                      <span className={iconWrapClass(activeDriverTab === 'account')}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </span>
                      <span className="text-[15px] font-medium">{tAccount}</span>
                    </span>
                    <ChevronIcon />
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  Menu
                </div>
                <div className="space-y-1 rounded-[22px] bg-white p-2 shadow-sm ring-1 ring-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      onHome?.();
                      handleClose();
                    }}
                    className={menuItemClass(false)}
                  >
                    <span className="flex items-center gap-3">
                      <span className={iconWrapClass(false)}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </span>
                      <span className="text-[15px] font-medium">{tHome}</span>
                    </span>
                    <ChevronIcon />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onRides?.();
                      handleClose();
                    }}
                    className={menuItemClass(false)}
                  >
                    <span className="flex items-center gap-3">
                      <span className={iconWrapClass(false)}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </span>
                      <span className="text-[15px] font-medium">{tMyRides}</span>
                    </span>
                    <ChevronIcon />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onProfile?.();
                      handleClose();
                    }}
                    className={menuItemClass(false)}
                  >
                    <span className="flex items-center gap-3">
                      <span className={iconWrapClass(false)}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </span>
                      <span className="text-[15px] font-medium">{tProfile}</span>
                    </span>
                    <ChevronIcon />
                  </button>
                </div>
              </>
            )}

            {isDriver ? (
              onUserDashboard && (
                <div className="rounded-[22px] bg-white p-2 shadow-sm ring-1 ring-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      onUserDashboard();
                      handleClose();
                    }}
                    className={menuItemClass(false)}
                  >
                    <span className="flex items-center gap-3">
                      <span className={iconWrapClass(false)}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </span>
                      <span className="text-[15px] font-medium">{tUserDashboard}</span>
                    </span>
                    <ChevronIcon />
                  </button>
                </div>
              )
            ) : (
              <>
                {user?.driverId ? (
                  onDriverDashboard && (
                <div className="rounded-[22px] bg-emerald-50 p-2 shadow-sm ring-1 ring-emerald-100">
                      <button
                        type="button"
                        onClick={() => {
                          onDriverDashboard();
                          handleClose();
                        }}
                        className="flex w-full touch-manipulation items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-green-700 active:bg-white/40"
                      >
                        <span className="flex items-center gap-3">
                          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/75 text-green-700">
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </span>
                          <span className="text-[15px] font-medium">{tDriverDashboard}</span>
                        </span>
                        <ChevronIcon className="text-green-500/60" />
                      </button>
                    </div>
                  )
                ) : (
                  onBecomeDriver && (
                <div className="rounded-[22px] bg-cyan-50 p-2 shadow-sm ring-1 ring-cyan-100">
                      <button
                        type="button"
                        onClick={() => {
                          onBecomeDriver();
                          handleClose();
                        }}
                        className="flex w-full touch-manipulation items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-primary-600 active:bg-white/45"
                      >
                        <span className="flex items-center gap-3">
                          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/80 text-primary-600">
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </span>
                          <span className="text-[15px] font-medium">{tBecomeDriver}</span>
                        </span>
                        <ChevronIcon className="text-primary-500/60" />
                      </button>
                    </div>
                  )
                )}
              </>
            )}

            <div className="rounded-[22px] bg-white p-2 shadow-sm ring-1 ring-slate-200">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(true)}
                className="flex w-full touch-manipulation items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-red-600 active:bg-red-50"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-medium">{tLogout}</span>
                </span>
                <ChevronIcon className="text-red-300" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          onLogout?.();
          handleClose();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );

  if (typeof document === 'undefined') {
    return sidebarContent;
  }

  return createPortal(sidebarContent, document.body);
};

function ChevronIcon({ className = 'text-slate-300' }) {
  return (
    <svg className={`h-5 w-5 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

export default memo(UDsidebar);
